/**
 * Corporate income tax — single-rate by category.
 *
 * Standard companies: 25%. Banks/insurance/telecom/petroleum/capital-market/
 * tobacco-alcohol: 30%. Special manufacturing industries: 20%. Export: 20%.
 *
 * @example
 * calculateCorporateTax({ profit: 10_000_000, category: "standard" });
 * // { tax: 2500000, rate: 0.25, ... }
 */

import { getRates } from "./data/index.js";
import type { CorporateCategory, FiscalYear } from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** Options for corporate tax. */
export interface CorporateTaxOptions {
  /** Net profit before tax (NPR). */
  readonly profit: number;
  /** Industry category. */
  readonly category: CorporateCategory;
  /** Fiscal year (default current). */
  readonly fiscalYear?: FiscalYear;
}

/** Corporate tax result. */
export interface CorporateTaxResult {
  readonly fiscalYear: FiscalYear;
  readonly category: CorporateCategory;
  readonly profit: number;
  readonly rate: number;
  readonly tax: number;
  readonly netAfterTax: number;
}

/** Calculate corporate income tax. */
export function calculateCorporateTax(opts: CorporateTaxOptions): CorporateTaxResult {
  ensureNonNegative(opts.profit, "calculateCorporateTax");
  const rates = getRates(opts.fiscalYear);
  const rate = rates.corporate[opts.category];
  const tax = r2(opts.profit * rate);
  return {
    fiscalYear: rates.fiscalYear,
    category: opts.category,
    profit: r2(opts.profit),
    rate,
    tax,
    netAfterTax: r2(opts.profit - tax),
  };
}

/** Get the corporate rate for a category at a given FY. */
export function getCorporateRate(
  category: CorporateCategory,
  fiscalYear?: FiscalYear,
): number {
  return getRates(fiscalYear).corporate[category];
}
